import React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import styled, { css } from "styled-components/macro";

// Departure: Mark's Button (src/redesign/components/ui/button.jsx on redesign-tailwind-theme), so his variants and only his default and icon sizes.
// Departure: hover and focus tints that need color-mix() or /opacity, newer than our browser floor, take the nearest theme colour or are left out.
// Departure: buttonVariants is a css fragment, not a cva function; style a link with it and give the link data-variant and data-size.

function ButtonElement({ variant = "default", size = "default", ...props }) {
  return <ButtonPrimitive data-slot="button" data-variant={variant} data-size={size} {...props} />;
}

// Every variant sets border-color, which outranks GlobalStyle's a:hover underline on a link
const buttonVariants = css`
  display: inline-flex;
  box-sizing: border-box;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: var(--radius-4xl);
  background-clip: padding-box;
  font-family: inherit;
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  font-weight: var(--juno-font-weight-regular);
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  outline: none;

  /* The default size, here so a page's styled(Button) can override it */
  height: var(--button-height, var(--juno-space-12));
  /* shadcn's gap-1.5, which has no --juno-space step */
  gap: calc(var(--juno-space-1) * 1.5);
  padding: 0 var(--juno-space-6);

  @media (prefers-reduced-motion: no-preference) {
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  svg {
    flex-shrink: 0;
    pointer-events: none;
  }

  svg:not([class*="size-"]) {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
  }

  &[data-size="default"]:has([data-icon="inline-end"]) {
    padding-right: var(--juno-space-5);
  }

  &[data-size="default"]:has([data-icon="inline-start"]) {
    padding-left: var(--juno-space-5);
  }

  &[data-size="icon"] {
    width: var(--button-height, var(--juno-space-12));
    padding: 0;
  }

  &[data-variant="default"] {
    border-color: transparent;
    background-color: var(--muted);
    color: var(--foreground);
  }

  /* Mark's foreground/10, which is --border */
  &[data-variant="default"]:hover {
    background-color: var(--border);
  }

  &[data-variant="outline"] {
    border-color: var(--button-outline-border-color, var(--border));
    background-color: var(--card);
    color: var(--button-outline-color, var(--foreground));
  }

  &[data-variant="outline"]:hover,
  &[data-variant="outline"][aria-expanded="true"] {
    background-color: var(--button-outline-hover-background, var(--muted));
    color: var(--button-outline-color, var(--foreground));
  }

  &[data-variant="ghost"]:hover,
  &[data-variant="ghost"][aria-expanded="true"] {
    background-color: var(--muted);
    color: var(--foreground);
  }

  &[data-variant="secondary"] {
    border-color: transparent;
    background-color: var(--button-secondary-background, var(--secondary));
    color: var(--button-secondary-color, var(--secondary-foreground));
  }

  &[data-variant="secondary"]:hover {
    background-color: var(--button-secondary-hover-background, var(--button-secondary-background, var(--secondary)));
  }

  &[data-variant="ghost"] {
    border-color: transparent;
    background-color: transparent;
    color: var(--foreground);
  }

  &[data-variant="destructive"] {
    border-color: transparent;
    background-color: var(--destructive);
    color: var(--card);
  }

  &[data-variant="link"] {
    border-color: transparent;
    background-color: transparent;
    color: var(--primary);
    /* shadcn's underline-offset-4 */
    text-underline-offset: 4px;
  }

  &[data-variant="link"]:hover {
    text-decoration: underline;
  }

  /* After the variants, so focus and errors replace their border colour */
  &:focus-visible {
    border-color: var(--ring);
    /* shadcn's ring-3 */
    box-shadow: 0 0 0 3px var(--ring);
  }

  &[aria-invalid="true"] {
    border-color: var(--destructive);
  }

  &:active:not([aria-haspopup]) {
    transform: translateY(1px);
  }

  &:disabled,
  &[data-disabled] {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const Button = styled(ButtonElement)`
  ${buttonVariants}
`;

export { Button, buttonVariants };
